import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { titleCase } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";

function PatientSelect({ value, onChange }) {
  const [patients, setPatients] = useState([]);
  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
  }, []);
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid="field-patient_id"><SelectValue placeholder="Select patient" /></SelectTrigger>
      <SelectContent>
        {patients.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            {p.full_name} · {p.patient_number || p.id}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RemoteSelect({ field, form, value, onChange }) {
  const [opts, setOpts] = useState([]);
  const path = field.endpoint(form);
  useEffect(() => {
    if (!path) { setOpts([]); return; }
    api.get(path).then((r) => setOpts(Array.isArray(r.data) ? r.data : [])).catch(() => setOpts([]));
  }, [path]);
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={`field-${field.name}`}><SelectValue placeholder={path ? (opts.length ? "Select" : "None available") : "Choose type first"} /></SelectTrigger>
      <SelectContent>
        {opts.map((o) => <SelectItem key={o.id} value={o.id}>{field.labelOf ? field.labelOf(o) : o.name || o.id}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function toInputDateTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RecordForm({ open, onOpenChange, title, fields, initial, onSubmit, submitting }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => {
        base[f.name] = initial?.[f.name] ?? f.default ?? "";
      });
      setForm(base);
    }
  }, [open, initial, fields]);

  const set = (k, v) => setForm((s) => {
    const next = { ...s, [k]: v };
    fields.forEach((f) => { if (f.dependsOn === k) next[f.name] = ""; });
    return next;
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    const missing = fields.find((f) => f.required && !String(payload[f.name] ?? "").trim());
    if (missing) { toast.error(`${missing.label} is required`); return; }
    fields.forEach((f) => {
      if (payload[f.name] === "" && !f.required) delete payload[f.name];
      if (f.type === "number" && payload[f.name] !== "" && payload[f.name] != null) payload[f.name] = Number(payload[f.name]);
      if (f.type === "datetime" && payload[f.name]) payload[f.name] = new Date(payload[f.name]).toISOString();
    });
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-hidden p-0">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="font-display text-lg">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="max-h-[62vh] space-y-4 overflow-y-auto px-6 py-5">
            {fields.map((f) => (
              <div key={f.name} className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {f.label}{f.required && <span className="text-rose-500"> *</span>}
                </Label>
                {f.type === "patient" ? (
                  <PatientSelect value={form[f.name]} onChange={(v) => set(f.name, v)} />
                ) : f.type === "remote" ? (
                  <RemoteSelect field={f} form={form} value={form[f.name]} onChange={(v) => set(f.name, v)} />
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} rows={3} required={f.required} />
                ) : f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                    <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{titleCase(o)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`field-${f.name}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text"}
                    value={f.type === "datetime" ? toInputDateTime(form[f.name]) : form[f.name] || ""}
                    onChange={(e) => set(f.name, e.target.value)}
                    required={f.required}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="border-t border-slate-100 px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-teal-700 hover:bg-teal-800" data-testid="form-submit">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
